import { TokenData } from "../../auth/types";
import { TFactor } from "../../models/Factors";
import { request } from "./request";

export const requestSms = async (phone: string): Promise<{ salt: string }> => {
  const response = await request({
    path: "/api/v1/user-service/request-code",
    method: "POST",
    body: {
      phone,
    },
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const authWithCode = async (
  phone: string,
  code: string,
  salt: string
): Promise<TokenData> => {
  const response = await request({
    path: "/api/v1/user-service/authuser-with-code",
    method: "POST",
    body: {
      phone,
      code,
      salt,
    },
  });

  if (response.ok) {
    return response.data.token;
  }

  throw new Error(response.errorMessage);
};

export const authSuperAdmin = async (
  email: string,
  password: string
): Promise<{ token: TokenData; isSuperAdmin: boolean }> => {
  const response = await request({
    path: "/api/v1/user-service/auth/super-admin",
    method: "POST",
    body: {
      email,
      password,
    },
  });

  if (response.ok) {
    return {
      token: response.data.token,
      isSuperAdmin: response.data.isSuperAdmin,
    };
  }

  throw new Error(response.errorMessage);
};

export const REFRESH_PATH = "/api/v1/user-service/refresh-token";

export const authRefresh = async (refreshToken: string): Promise<TokenData> => {
  const response = await request({
    path: REFRESH_PATH,
    method: "POST",
    body: {
      refreshToken,
    },
  });

  if (response.ok) {
    return response.data.token;
  }

  throw new Error(response.errorMessage);
};

export const userLogout = async (): Promise<void> => {
  const response = await request({
    path: "/api/v1/user-service/logout",
    method: "POST",
  });

  if (!response.ok) throw new Error(response.errorMessage);
};

export const startLogin = async (
  flow: "LOGIN_FLOW_COCKPIT" | "LOGIN_FLOW_USER_APP",
  identity: string,
  verificationRequestId?: number,
  verificationRequestToken?: string
): Promise<{
  authSessionId: string;
  process:
    | "AUTH_PROCESS_UNSPECIFIED"
    | "AUTH_PROCESS_SIGN_IN"
    | "AUTH_PROCESS_SIGN_UP";
  factor: TFactor;
}> => {
  const response = await request({
    path: "/api/v1/user-service/auth/start-login",
    method: "POST",
    body: {
      flow,
      identity,
      verification_request_id: verificationRequestId,
      verification_request_token: verificationRequestToken,
    },
  });

  if (response.ok) {
    const data = response.data;
    return {
      authSessionId: data.authSessionId,
      process: data.process,
      factor: {
        factorId: data.factor.factorId,
        factorType: data.factor.factorType,
        factorInfoMasked: data.factor.factorInfoMasked,
      },
    };
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const verifyFactor = async (
  authSessionId: string,
  identity: string,
  factorId: string,
  code: string
): Promise<{
  authSessionId: string;
  session: {
    accessToken: string;
    refreshToken: string;
    created: string;
    expiry: string;
  };
  nextFactor?: TFactor;
}> => {
  const response = await request({
    path: "/api/v1/user-service/auth/verify-factor",
    method: "POST",
    body: {
      authSessionId,
      identity,
      factorId,
      code,
    },
  });

  if (response.ok) {
    return response.data;
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const resendFactor = async (
  authSessionId: string,
  identity: string,
  factorId: string
): Promise<{
  authSessionId: string;
  factor: TFactor;
}> => {
  const response = await request({
    path: "/api/v1/user-service/auth/resend-factor",
    method: "POST",
    body: {
      authSessionId,
      identity,
      factorId,
    },
  });

  if (response.ok) {
    return response.data;
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const getOperationsFactor = async (): Promise<{
  factor: TFactor;
}> => {
  const response = await request({
    path: "/api/v1/user-service/user/me/get-operations-factor",
    method: "GET",
  });

  if (response.ok) {
    return response.data;
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const sendOperationsFactor = async (): Promise<{
  operationsSessionId: string;
  factor: TFactor;
}> => {
  const response = await request({
    path: "/api/v1/user-service/user/me/send-operations-factor",
    method: "POST",
  });

  if (response.ok) {
    return response.data;
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const changePassword = async (
  operationsSessionId: string,
  factorId: string,
  code: string,
  newPassword: string
): Promise<void> => {
  const response = await request({
    path: "/api/v1/user-service/auth/change-password",
    method: "POST",
    body: {
      operationsSessionId,
      factorId,
      code,
      newPassword,
    },
  });

  if (response.ok) {
    return;
  }

  throw response.apiError || new Error(response.errorMessage);
};
